import { useState, useEffect, useCallback } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { taskService, userService, projectService } from '@/services'
import { toast } from 'sonner'
import type { Project, User, Task } from '@/types'
import { LoadingState, PageContainer, PageHeader } from '@/components/layout'
import { useUnsavedChanges } from '@/hooks/useUnsavedChanges'
import { UnsavedChangesDialog } from '@/components/ui/unsaved-changes-dialog'
import { TaskForm, type TaskFormData } from '@/components/tasks/TaskForm'

export default function TaskEditPage() {
  const { taskId } = useParams<{ taskId: string }>()
  const navigate = useNavigate()
  const [loading, setLoading] = useState(false)
  const [initialLoading, setInitialLoading] = useState(true)
  const [initialData, setInitialData] = useState<Partial<TaskFormData>>()
  const [initialProjects, setInitialProjects] = useState<Project[]>([])
  const [initialDependencies, setInitialDependencies] = useState<Task[]>([])
  const [initialUsers, setInitialUsers] = useState<User[]>([])
  const [taskName, setTaskName] = useState<string>('')
  const [isDirty, setIsDirty] = useState(false)

  // Track unsaved changes
  const unsavedChanges = useUnsavedChanges({
    isDirty: isDirty && !loading,
  })

  const loadInitialData = useCallback(async () => {
    if (!taskId) return

    try {
      setInitialLoading(true)
      const task = await taskService.getById(taskId)
      if (!task) {
        toast.error('Task not found')
        navigate('/tasks')
        return
      }

      // Set task name for header
      setTaskName(task.name)

      // Set initial form data
      setInitialData({
        type: task.type,
        name: task.name,
        description: task.description,
        status: task.status,
        priority: task.priority,
        horizon: task.horizon,
        dueDate: task.dueDate ? new Date(task.dueDate) : undefined,
        assigneeId: task.assignee?.id,
        workspaceId: task.workspace.id,
      })

      // Set selected projects, dependencies, and users
      setInitialProjects(task.projects)
      setInitialDependencies(task.dependencies)
      setInitialUsers(task.users)
    } catch (error) {
      console.error('Failed to load task:', error)
      toast.error('Failed to load task')
      navigate('/tasks')
    } finally {
      setInitialLoading(false)
    }
  }, [taskId, navigate])

  useEffect(() => {
    loadInitialData()
  }, [loadInitialData])

  const handleSubmit = async (data: TaskFormData) => {
    if (!taskId) return

    setLoading(true)
    try {
      // Get updated projects, dependencies, and users
      const projects = data.projectIds
        ? await Promise.all(data.projectIds.map((id) => projectService.getById(id)))
        : []
      const dependencies = data.dependencyIds
        ? await Promise.all(data.dependencyIds.map((id) => taskService.getById(id)))
        : []
      const users = data.userIds
        ? await Promise.all(data.userIds.map((id) => userService.getById(id)))
        : []

      await taskService.update(taskId, {
        type: data.type,
        name: data.name,
        description: data.description,
        status: data.status,
        priority: data.priority,
        horizon: data.horizon,
        dueDate: data.dueDate,
        assignee: data.assigneeId ? await userService.getById(data.assigneeId) : undefined,
        projects: projects.filter((p): p is Project => p !== undefined),
        dependencies: dependencies.filter((t): t is Task => t !== undefined),
        users: users.filter((u): u is User => u !== undefined),
      } as Partial<Task>)

      // Allow navigation before resetting dirty state and navigating
      unsavedChanges.allowNavigation()
      setIsDirty(false)

      toast.success('Task updated successfully')
      navigate(`/tasks/${taskId}`)
    } catch (error) {
      console.error('Failed to update task:', error)
      toast.error('Failed to update task')
    } finally {
      setLoading(false)
    }
  }

  const handleCancel = () => {
    navigate(`/tasks/${taskId}`)
  }

  if (initialLoading || !initialData) {
    return (
      <PageContainer maxWidth="full">
        <LoadingState variant="spinner" text="Loading task..." />
      </PageContainer>
    )
  }

  return (
    <PageContainer maxWidth="full">
      <PageHeader title={taskName} titlePrefix="Editing Task:" />

      <TaskForm
        mode="edit"
        initialData={initialData}
        initialProjects={initialProjects}
        initialDependencies={initialDependencies}
        initialUsers={initialUsers}
        excludeTaskId={taskId}
        onSubmit={handleSubmit}
        onCancel={handleCancel}
        onDirtyChange={setIsDirty}
        loading={loading}
        title="Task Details"
        description="Update the task details below"
      />

      {/* Unsaved Changes Dialog */}
      <UnsavedChangesDialog
        open={unsavedChanges.showDialog}
        onOpenChange={(open) => !open && unsavedChanges.handleCancelNavigation()}
        onConfirm={unsavedChanges.handleConfirmNavigation}
        onCancel={unsavedChanges.handleCancelNavigation}
      />
    </PageContainer>
  )
}
